/**
 * Frogger config — the structured per-game data the SkinResolver consumes.
 *
 *   grid     : board geometry (cell size, columns, padding)
 *   tuning   : per-mechanic magic numbers
 *   modes    : mode definitions (id + label + overrides)
 *   settings : { key: { default, type, options?, label } } — defaults + OptionsScene UI metadata
 *   elements : asset slots { key: { label, footprint, defaultPath?, fallbackColor?, ... } }
 */

import { FROGGER_CONFIG } from "../src/games/frogger/froggerConfig.js";

export const FROGGER = {
  grid: {
    cellSize: FROGGER_CONFIG.CELL_SIZE,
    cols: FROGGER_CONFIG.COLS,
    playerSize: FROGGER_CONFIG.PLAYER_SIZE,
    playerScreenRow: FROGGER_CONFIG.PLAYER_SCREEN_ROW,
    cameraLerp: FROGGER_CONFIG.CAMERA_LERP,
    visibleBuffer: FROGGER_CONFIG.VISIBLE_BUFFER,
  },

  tuning: {
    safeIntervalRange: FROGGER_CONFIG.SAFE_INTERVAL_RANGE,
    speedRange: FROGGER_CONFIG.SPEED_RANGE,
    spawnRateRange: FROGGER_CONFIG.SPAWN_RATE_RANGE,
    spawnJitter: FROGGER_CONFIG.SPAWN_JITTER,
    minVehicleGap: FROGGER_CONFIG.MIN_VEHICLE_GAP,
    spawnEdgeClearance: FROGGER_CONFIG.SPAWN_EDGE_CLEARANCE,
    maxVehiclesPerRow: FROGGER_CONFIG.MAX_VEHICLES_PER_ROW,
    initialVehiclesRange: FROGGER_CONFIG.INITIAL_VEHICLES_RANGE,
    speedIncreasePer10Rows: FROGGER_CONFIG.SPEED_INCREASE_PER_10_ROWS,
    minSpawnRate: FROGGER_CONFIG.MIN_SPAWN_RATE,
    pointsPerNewRow: FROGGER_CONFIG.POINTS_PER_NEW_ROW,
    initialLives: FROGGER_CONFIG.INITIAL_LIVES,
    coinChance: FROGGER_CONFIG.COIN_CHANCE,
    coinPoints: FROGGER_CONFIG.COIN_POINTS,
    starIntervalRange: FROGGER_CONFIG.STAR_INTERVAL_RANGE,
    starPoints: FROGGER_CONFIG.STAR_POINTS,
    starMissRows: FROGGER_CONFIG.STAR_MISS_ROWS,
    starWarnRows: FROGGER_CONFIG.STAR_WARN_ROWS,
    speedMultipliers: FROGGER_CONFIG.SPEED_MULTIPLIERS,
    densityProfiles: FROGGER_CONFIG.DENSITY_PROFILES,
    birdSpawnInterval: FROGGER_CONFIG.BIRD_SPAWN_INTERVAL,
    birdSpeed: FROGGER_CONFIG.BIRD_SPEED,
    birdSize: FROGGER_CONFIG.BIRD_SIZE,
    abilitySpawnChance: FROGGER_CONFIG.ABILITY_SPAWN_CHANCE,
    abilityTypes: FROGGER_CONFIG.ABILITY_TYPES,
    dashRows: FROGGER_CONFIG.DASH_ROWS,
    dashRowsRange: FROGGER_CONFIG.DASH_ROWS_RANGE,
    slowtimeDuration: FROGGER_CONFIG.SLOWTIME_DURATION,
    slowtimeFactor: FROGGER_CONFIG.SLOWTIME_FACTOR,
    bombRadius: FROGGER_CONFIG.BOMB_RADIUS,
    laneChangeChance: FROGGER_CONFIG.LANE_CHANGE_CHANCE,
    laneChangeFrames: FROGGER_CONFIG.LANE_CHANGE_FRAMES,
    laneChangeCooldown: FROGGER_CONFIG.LANE_CHANGE_COOLDOWN,
    vehicleTypes: FROGGER_CONFIG.VEHICLE_TYPES,
  },

  modes: {
    fixed: { label: "Fixed Finish" },
    endless: { label: "Endless" },
  },

  settings: {
    gameMode: {
      default: "fixed",
      type: "choice",
      options: ["fixed", "infinite"],
      label: "Game Mode",
    },
    vehicleSpeed: {
      default: "normal",
      type: "choice",
      options: ["slow", "normal", "fast"],
      label: "Vehicle Speed",
    },
    vehicleDensity: {
      default: "few",
      type: "choice",
      options: ["few", "normal", "many", "max"],
      label: "Vehicle Density",
    },
    finishScoreGate: {
      default: 200,
      type: "number",
      min: 100,
      max: 1000,
      step: 50,
      label: "Score To Win",
    },
    bonusLives: { default: true, type: "boolean", label: "Bonus Lives" },
    smoothMovement: { default: false, type: "boolean", label: "Smooth Movement" },
    balloonTrails: { default: false, type: "boolean", label: "Balloon Confetti Trail" },
    birdsEnabled: { default: false, type: "boolean", label: "Bird Enemies" },
    birdTrails: { default: false, type: "boolean", label: "Bird Confetti Trail" },
    abilitiesEnabled: { default: false, type: "boolean", label: "Abilities" },
    laneChanging: {
      default: false,
      type: "boolean",
      label: "Vehicle Lane Changing",
    },
    timeAttack: { default: false, type: "boolean", label: "Time Attack" },
    timeAttackSeconds: {
      default: 40,
      type: "number",
      min: 10,
      max: 180,
      step: 5,
      label: "Time Attack Duration (s)",
    },
    dashRowsMin: {
      default: FROGGER_CONFIG.DASH_ROWS_RANGE[0],
      type: "number",
      min: 1,
      max: 30,
      step: 1,
      label: "Dash Min Rows",
    },
    dashRowsMax: {
      default: FROGGER_CONFIG.DASH_ROWS_RANGE[1],
      type: "number",
      min: 1,
      max: 30,
      step: 1,
      label: "Dash Max Rows",
    },
  },

  elements: {
    "atlas.main": {
      label: "Shared Atlas Source",
      category: "atlas",
      footprint: { w: 1, h: 1 },
    },
    background: { label: "Background", category: "board", fitMode: "cover" },
    sky: { label: "Sky Layer", category: "board", fitMode: "cover" },
    bottom: {
      label: "Start Area Background",
      category: "board",
      fitMode: "cover",
      defaultPath: "assets/frogger/game-bottom.png",
    },
    "side.panel": {
      label: "Side Panel / Border",
      category: "ui",
      fitMode: "cover",
    },
    "lane.road": {
      label: "Road Texture",
      category: "board",
      footprint: { w: 1, h: 1 },
      fitMode: "cover",
    },
    "lane.safe": {
      label: "Safe Lane Texture",
      category: "board",
      footprint: { w: 1, h: 1 },
      fitMode: "cover",
      defaultPath: "assets/frogger/safezone.png",
    },
    "lane.marking": {
      label: "Road Markings",
      category: "board",
      footprint: { w: 1, h: 1 },
      fitMode: "cover",
    },
    "decor.tree": {
      label: "Safe: Tree",
      category: "obstacles",
      footprint: { w: 1, h: 1 },
      defaultPath: "assets/frogger/decoration-tree.png",
    },
    "decor.pinetree": {
      label: "Safe: Pine Tree",
      category: "obstacles",
      footprint: { w: 1, h: 1 },
      defaultPath: "assets/frogger/decoration-pinetree.png",
    },
    "decor.bench": {
      label: "Safe: Bench",
      category: "obstacles",
      footprint: { w: 2, h: 1 },
      defaultPath: "assets/frogger/decoration-bench.png",
    },
    "decor.lamp": {
      label: "Safe: Lamp Post",
      category: "obstacles",
      footprint: { w: 1, h: 1 },
      defaultPath: "assets/frogger/decoration-lamp.png",
    },
    "decor.flowers": {
      label: "Safe: Flowers",
      category: "obstacles",
      footprint: { w: 1, h: 1 },
      defaultPath: "assets/frogger/decoration-flowers.png",
    },
    "decor.grass": {
      label: "Safe: Grass Tuft",
      category: "obstacles",
      footprint: { w: 1, h: 1 },
      defaultPath: "assets/frogger/decoration-grass.png",
    },
    "decor.bush": {
      label: "Safe: Bush",
      category: "obstacles",
      footprint: { w: 1, h: 1 },
    },
    "collectible.coin": {
      label: "Coin Icon",
      category: "pickups",
      footprint: { w: 1, h: 1 },
      fallbackColor: 0xfbbf24,
      defaultPath: "assets/frogger/collectible-coin.png",
    },
    "collectible.star": {
      label: "Star Icon",
      category: "pickups",
      footprint: { w: 1, h: 1 },
      fallbackColor: 0xec4899,
      defaultPath: "assets/frogger/collectible-star.png",
    },
    "ability.dash": {
      label: "Ability: Dash",
      category: "pickups",
      footprint: { w: 1, h: 1 },
      fallbackColor: 0x06b6d4,
      defaultPath: "assets/frogger/ability-dash.png",
    },
    "ability.slow": {
      label: "Ability: Slow Time",
      category: "pickups",
      footprint: { w: 1, h: 1 },
      fallbackColor: 0x8b5cf6,
      defaultPath: "assets/frogger/ability-slow.png",
    },
    "ability.bomb": {
      label: "Ability: Bomb",
      category: "pickups",
      footprint: { w: 1, h: 1 },
      fallbackColor: 0xf97316,
      defaultPath: "assets/frogger/ability-bomb.png",
    },
    "finish.building": {
      label: "Finish Building",
      category: "goal",
      footprint: { w: 3, h: 2 },
      fitMode: "cover",
      defaultPath: "assets/frogger/muzeum.png",
    },
    "finish.gate": {
      label: "Finish Gate",
      category: "goal",
      footprint: { w: 3, h: 1 },
    },
    player: {
      label: "Balloon Player",
      category: "characters",
      footprint: { w: 1, h: 1 },
      fallbackColor: 0x22c55e,
      defaultPath: "assets/frogger/player.png",
    },
    "vehicle.bike": {
      label: "Bike",
      category: "characters",
      footprint: { w: 1, h: 1 },
      fallbackColor: 0x10b981,
      defaultPaths: [
        "assets/frogger/vehicle-bike-blue.png",
        "assets/frogger/vehicle-bike-purple.png",
        "assets/frogger/vehicle-bike-red.png",
      ],
    },
    "vehicle.car": {
      label: "Car",
      category: "characters",
      footprint: { w: 2, h: 1 },
      fallbackColor: 0xef4444,
      defaultPaths: [
        "assets/frogger/vehicle-car-blue.png",
        "assets/frogger/vehicle-car-green.png",
        "assets/frogger/vehicle-car-purple.png",
        "assets/frogger/vehicle-car-red.png",
      ],
    },
    "vehicle.truck": {
      label: "Truck",
      category: "characters",
      footprint: { w: 2, h: 1 },
      fallbackColor: 0xf97316,
      defaultPath: "assets/frogger/vehicle-truck.png",
    },
    "vehicle.bus": {
      label: "Tram / Bus",
      category: "characters",
      footprint: { w: 3, h: 1 },
      fallbackColor: 0x3b82f6,
      defaultPath: "assets/frogger/vehicle-bus.png",
    },
    bird: {
      label: "Bird Enemy",
      category: "characters",
      footprint: { w: 2, h: 1 },
      fallbackColor: 0xdc2626,
      defaultPaths: [
        "assets/frogger/bird-blue.png",
        "assets/frogger/bird-pink.png",
        "assets/frogger/bird-purple.png",
        "assets/frogger/bird-red.png",
        "assets/frogger/bird-yellow.png",
      ],
    },
    "hud.panel": { label: "HUD Panel", category: "ui", fitMode: "cover" },
    "hud.life": {
      label: "HUD Life Icon",
      category: "ui",
      defaultPath: "assets/frogger/game-life-heart.png",
    },
    "hud.timer": {
      label: "HUD Timer Icon",
      category: "ui",
      defaultPath: "assets/frogger/game-time-clock.png",
    },
    "hud.score": {
      label: "HUD Score Icon",
      category: "ui",
      defaultPath: "assets/frogger/game-score-cup.png",
    },
    "hud.row": {
      label: "HUD Row Icon",
      category: "ui",
      defaultPath: "assets/frogger/game-row-star.png",
    },
    controls: {
      label: "On-Screen Controls",
      category: "ui",
      fitMode: "contain",
      defaultPath: "assets/frogger/controls.png",
    },
  },

  palette: {
    safeZone: FROGGER_CONFIG.COLOR_SAFE_ZONE,
    road: FROGGER_CONFIG.COLOR_ROAD,
    laneLine: FROGGER_CONFIG.COLOR_LANE_LINE,
    coin: FROGGER_CONFIG.COIN_COLOR,
    star: FROGGER_CONFIG.STAR_COLOR,
    bird: FROGGER_CONFIG.BIRD_COLOR,
    abilities: FROGGER_CONFIG.ABILITY_COLORS,
  },
};
